// Third-party
// MUI
import { Box, Grid, Skeleton, Stack, Typography } from "@mui/material";

// Hooks
import { useEffect, useRef } from "react";
import { useLocation } from "@Common/hooks/Location/useLocation";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import FinanceDealsTable from "@DataPages/components/FniManagement/components/FinanceDeals/FinanceDealsTable";

// Assets
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Types
import { Nullable } from "@Common/interfaces/types";
import { IFinanceDeal } from "@DataPages/types/financeDeals/model";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

interface IFinanceDealsModelProps {
	selected: IPeriodTreeSelection;
	locationId: string;
	data: Nullable<IFinanceDeal[]>;
	isLoading: boolean;
}

function FinanceDealsBody(props: Readonly<IFinanceDealsModelProps>) {
	const { selected, isLoading, data: financeDeals } = props;
	const subscriptionId = useRef<string | null>(null);

	const location = useLocation();

	useEffect(() => {
		if (selected) {
			subscriptionId.current = selected._id;
		}

		return () => {
			subscriptionId.current = null;
		};
	}, [props, location]);

	const titleContent = isLoading ? (
		<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
	) : (
		<>
			{selected && (
				<Typography className={"page-title"} sx={{ lineHeight: "1.2" }}>
					{`${selected?.titleExtended} F&I Deal Data`}
				</Typography>
			)}
		</>
	);

	let content;
	if (isLoading) {
		content = (
			<Grid item xs={12} mt={1}>
				<TableLoader toolbar buttons={3} content rows={5} multiTitles={5}></TableLoader>
			</Grid>
		);
	} else if (!financeDeals?.length) {
		content = (
			<EmptyBody
				image={selected ? emptyTable : empty}
				title={selected ? "No F&I Deal Data for this month" : "No Pay Period selected"}
				bodyHeight={selected ? "435px" : "510px"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	} else {
		content = <FinanceDealsTable data={financeDeals ?? []} />;
	}

	return (
		<Box className="py-8">
			<Stack display={"flex"} direction={"column"}>
				<Typography component={"div"}>{titleContent}</Typography>
				<Box height={600} mt={1}>
					<Stack display={"flex"} direction={"column"}>
						{content}
					</Stack>
				</Box>
			</Stack>
		</Box>
	);
}

export default FinanceDealsBody;
